/**
 * StatusPills - reusable status filter pills for list pages.
 *
 * Renders rounded-full pill buttons with counts, driven by API data.
 * "All" pill is always first. Active pill is solid green; inactive is subtle.
 * Used on Registrations, Claims, and future entity list pages.
 */

import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/components/ui/button'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'

interface StatusPillItem {
  key: string
  label: string
  count: number
}

interface StatusPillsProps {
  items: StatusPillItem[]
  activeKey: string | null
  onSelect: (key: string | null) => void
  totalCount: number
  totalLabel?: string
  /** When provided, each status pill (not "All") shows a dismiss × that hides it. */
  onDismiss?: (key: string) => void
  /**
   * Maximum number of status pills rendered inline before the rest collapse
   * into a "More" dropdown. Defaults to 5.
   */
  maxVisible?: number
}

export type { StatusPillItem }

export function StatusPills({
  items,
  activeKey,
  onSelect,
  totalCount,
  totalLabel = 'All',
  onDismiss,
  maxVisible = 5,
}: StatusPillsProps) {
  const { t } = useTranslation()
  const visibleItems = items.slice(0, maxVisible)
  const overflowItems = items.slice(maxVisible)
  // Keep the active pill reachable: if it lives in the overflow set, the "More"
  // trigger reflects the active state so the current filter is never hidden.
  const overflowHasActive = overflowItems.some((item) => item.key === activeKey)

  return (
    <>
      {/* "All" pill - always first */}
      <Button
        variant="ghost"
        onClick={() => onSelect(null)}
        className={cn(
          'shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-normal uppercase tracking-[0.07em] transition-all duration-[120ms]',
          activeKey === null
            ? 'border-[0.5px] border-transparent bg-[var(--green)] text-white hover:bg-[var(--green)] hover:text-white'
            : 'border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)] hover:border-[var(--rule-heavy)] hover:bg-[var(--bg-subtle)] hover:text-[var(--ink-2)]'
        )}
      >
        {totalLabel}{' '}
        <span className="ml-0.5 text-xs opacity-65">{totalCount}</span>
      </Button>

      {/* Status pills */}
      {visibleItems.map((item) => {
        const isActive = activeKey === item.key
        // The select pill and the dismiss control are sibling buttons
        // inside a non-interactive container - nesting an interactive
        // element inside a <button> is invalid HTML. The container carries
        // the pill's border/background; the inner Buttons stay transparent.
        return (
          <div
            key={item.key}
            className={cn(
              'group/pill inline-flex shrink-0 items-center whitespace-nowrap rounded-lg transition-all duration-[120ms]',
              isActive
                ? 'border-[0.5px] border-transparent bg-[var(--green)] text-white'
                : 'border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-2)] hover:border-[var(--rule-heavy)]'
            )}
          >
            <Button
              variant="ghost"
              aria-pressed={isActive}
              onClick={() => onSelect(isActive ? null : item.key)}
              className={cn(
                'rounded-lg py-1.5 pl-3 text-sm font-normal uppercase tracking-[0.07em] hover:bg-transparent',
                onDismiss ? 'pr-1.5' : 'pr-3',
                isActive
                  ? 'text-white hover:text-white'
                  : 'text-[var(--ink-2)] hover:text-[var(--ink-2)]'
              )}
            >
              {item.label}{' '}
              <span className="ml-0.5 text-xs opacity-65">{item.count}</span>
            </Button>
            {onDismiss && (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={t('common.hideItem', 'Hide {{label}}', {
                  label: item.label,
                })}
                onClick={() => onDismiss(item.key)}
                className={cn(
                  'mr-1 h-4 w-4 rounded-full text-base leading-none opacity-50 hover:bg-transparent hover:opacity-100',
                  isActive ? 'text-white hover:text-white' : 'text-[var(--ink-2)]'
                )}
              >
                &times;
              </Button>
            )}
          </div>
        )
      })}

      {/* Overflow "More" dropdown - remaining pills collapse in here */}
      {overflowItems.length > 0 && (
        <Popover>
          <PopoverTrigger
            render={
              <Button
                variant="ghost"
                aria-label={t('common.moreFilters', 'More status filters')}
                className={cn(
                  'shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-normal uppercase tracking-[0.07em] transition-all duration-[120ms]',
                  overflowHasActive
                    ? 'border-[0.5px] border-transparent bg-[var(--green)] text-white hover:bg-[var(--green)] hover:text-white'
                    : 'border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)] hover:border-[var(--green)] hover:bg-[var(--green-bg)] hover:text-[var(--green)]'
                )}
              >
                {t('common.more', 'More')}
                <span className="ml-0.5 text-xs opacity-65">
                  {overflowItems.length}
                </span>
              </Button>
            }
          />
          <PopoverContent
            side="bottom"
            align="start"
            className="w-auto min-w-[240px] max-w-[360px] p-1"
          >
            <div className="flex max-h-[320px] flex-col overflow-y-auto">
              {overflowItems.map((item) => {
                const isActive = activeKey === item.key
                return (
                  <div
                    key={item.key}
                    className="group/row flex items-center gap-1 rounded-[6px] pr-1 hover:bg-[var(--bg-subtle)]"
                  >
                    <Button
                      variant="ghost"
                      onClick={() => onSelect(isActive ? null : item.key)}
                      className="flex h-auto flex-1 items-center justify-start gap-3 rounded-[6px] px-2 py-1.5 text-left text-[13px] font-normal uppercase tracking-[0.07em] text-[var(--ink-2)] hover:bg-transparent hover:text-[var(--ink-2)]"
                    >
                      <span className="flex w-4 shrink-0 justify-center text-[var(--green)]">
                        {isActive && <Check className="h-3.5 w-3.5" />}
                      </span>
                      <span className="flex-1 whitespace-nowrap text-left">
                        {item.label}
                      </span>
                      <span className="shrink-0 text-xs opacity-65">
                        {item.count}
                      </span>
                    </Button>
                    {onDismiss && (
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={t('common.hideItem', 'Hide {{label}}', {
                          label: item.label,
                        })}
                        onClick={() => onDismiss(item.key)}
                        className="h-4 w-4 rounded-full text-base leading-none text-[var(--ink-2)] opacity-50 hover:bg-transparent hover:opacity-100"
                      >
                        &times;
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}
    </>
  )
}
